/**
 * GalleryZoom adds mobile-only pinch + double-tap zoom/pan to the product media gallery's
 * main viewer, and disables the native swipe-to-next-slide behavior while a slide is zoomed.
 * It also blocks the tap-to-open-lightbox behavior on mobile, since zooming now happens inline.
 * Desktop/tablet (>=750px) behavior is untouched.
 */
if (!window.GalleryZoom) {
  window.GalleryZoom = class GalleryZoom {
    constructor(gallery) {
      this.gallery = gallery;
      this.viewer = gallery.elements.viewer;
      this.slider = this.viewer && this.viewer.querySelector('[id^="Slider-"]');
      if (!this.slider) return;

      this.mql = gallery.mql;
      this.maxScale = 3;
      this.doubleTapScale = 2.5;
      this.zoomEnabled = gallery.dataset.imageZoom !== 'none';

      this.state = new WeakMap();
      this.pointers = new Map();
      this.activeContainer = null;
      this.mode = null;
      this.pinchStart = null;
      this.lastPanPoint = null;
      this.tapStart = null;
      this.lastTap = null;

      this.slider.addEventListener('pointerdown', this.onPointerDown.bind(this));
      this.slider.addEventListener('pointermove', this.onPointerMove.bind(this));
      this.slider.addEventListener('pointerup', this.onPointerUp.bind(this));
      this.slider.addEventListener('pointercancel', this.onPointerUp.bind(this));

      this.viewer.addEventListener('click', this.onClickCapture.bind(this), true);
      this.viewer.addEventListener('slideChanged', this.resetAll.bind(this));
    }

    isMobile() {
      return !this.mql.matches;
    }

    getContainer(target) {
      return target.closest && target.closest('.product-media-container.media-type-image');
    }

    getState(container) {
      let state = this.state.get(container);
      if (!state) {
        state = { scale: 1, x: 0, y: 0 };
        this.state.set(container, state);
      }
      return state;
    }

    applyTransform(container) {
      const img = container.querySelector('.product__media img');
      if (!img) return;
      const state = this.getState(container);
      img.style.transformOrigin = '0 0';
      img.style.transform = `translate(${state.x}px, ${state.y}px) scale(${state.scale})`;
    }

    clamp(state, rect) {
      const maxOffsetX = Math.max(0, rect.width * (state.scale - 1));
      const maxOffsetY = Math.max(0, rect.height * (state.scale - 1));
      state.x = Math.min(0, Math.max(-maxOffsetX, state.x));
      state.y = Math.min(0, Math.max(-maxOffsetY, state.y));
    }

    setZoomActive(active) {
      this.slider.classList.toggle('gallery-zoom-active', active);
    }

    announce(zoomedIn) {
      const liveRegion = this.gallery.elements.liveRegion;
      if (!liveRegion || !window.accessibilityStrings) return;
      liveRegion.setAttribute('aria-hidden', 'false');
      liveRegion.textContent = zoomedIn
        ? window.accessibilityStrings.imageZoomedIn
        : window.accessibilityStrings.imageZoomedOut;
      clearTimeout(this.announceTimeout);
      this.announceTimeout = setTimeout(() => {
        liveRegion.setAttribute('aria-hidden', 'true');
      }, 1500);
    }

    resetContainer(container) {
      const state = this.getState(container);
      if (state.scale === 1 && state.x === 0 && state.y === 0) return;
      state.scale = 1;
      state.x = 0;
      state.y = 0;
      const img = container.querySelector('.product__media img');
      if (img) {
        img.style.transform = '';
        img.style.transformOrigin = '';
      }
    }

    resetAll() {
      if (!this.slider) return;
      this.slider.querySelectorAll('.product-media-container.media-type-image').forEach((container) => {
        this.resetContainer(container);
      });
      this.setZoomActive(false);
      this.pointers.clear();
      this.mode = null;
      this.pinchStart = null;
      this.lastPanPoint = null;
      this.tapStart = null;
    }

    distance(p1, p2) {
      return Math.hypot(p2.x - p1.x, p2.y - p1.y);
    }

    midpoint(p1, p2) {
      return { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
    }

    onPointerDown(event) {
      if (!this.isMobile() || !this.zoomEnabled) return;
      const container = this.getContainer(event.target);
      if (!container) return;

      if (this.activeContainer && this.activeContainer !== container) {
        this.pointers.clear();
      }
      this.activeContainer = container;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (this.pointers.size === 1) {
        this.tapStart = { time: Date.now(), x: event.clientX, y: event.clientY, container };
      }

      if (this.pointers.size === 2) {
        const [p1, p2] = Array.from(this.pointers.values());
        const rect = container.querySelector('.product__media').getBoundingClientRect();
        const state = this.getState(container);
        this.pinchStart = {
          distance: this.distance(p1, p2),
          scale: state.scale,
          midpoint: this.midpoint({ x: p1.x - rect.left, y: p1.y - rect.top }, { x: p2.x - rect.left, y: p2.y - rect.top }),
          origin: { x: state.x, y: state.y },
          rect,
        };
        this.mode = 'pinch';
        this.tapStart = null;
        this.setZoomActive(true);
        event.preventDefault();
      } else if (this.pointers.size === 1 && this.getState(container).scale > 1) {
        this.mode = 'pan';
        this.lastPanPoint = { x: event.clientX, y: event.clientY };
        this.setZoomActive(true);
      }
    }

    onPointerMove(event) {
      if (!this.pointers.has(event.pointerId)) return;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (this.mode === 'pinch' && this.pointers.size === 2 && this.pinchStart) {
        event.preventDefault();
        const [p1, p2] = Array.from(this.pointers.values());
        const rect = this.pinchStart.rect;
        const currentDistance = this.distance(p1, p2);
        const currentMid = this.midpoint({ x: p1.x - rect.left, y: p1.y - rect.top }, { x: p2.x - rect.left, y: p2.y - rect.top });
        const newScale = Math.min(
          this.maxScale,
          Math.max(1, this.pinchStart.scale * (currentDistance / this.pinchStart.distance))
        );
        const imagePoint = {
          x: (this.pinchStart.midpoint.x - this.pinchStart.origin.x) / this.pinchStart.scale,
          y: (this.pinchStart.midpoint.y - this.pinchStart.origin.y) / this.pinchStart.scale,
        };
        const state = this.getState(this.activeContainer);
        state.scale = newScale;
        state.x = currentMid.x - imagePoint.x * newScale;
        state.y = currentMid.y - imagePoint.y * newScale;
        this.clamp(state, rect);
        this.applyTransform(this.activeContainer);
      } else if (this.mode === 'pan' && this.pointers.size === 1 && this.activeContainer) {
        event.preventDefault();
        const state = this.getState(this.activeContainer);
        const last = this.lastPanPoint || { x: event.clientX, y: event.clientY };
        state.x += event.clientX - last.x;
        state.y += event.clientY - last.y;
        const rect = this.activeContainer.querySelector('.product__media').getBoundingClientRect();
        this.clamp(state, rect);
        this.applyTransform(this.activeContainer);
        this.lastPanPoint = { x: event.clientX, y: event.clientY };
      } else if (this.tapStart && this.pointers.size === 1) {
        const moved = Math.hypot(event.clientX - this.tapStart.x, event.clientY - this.tapStart.y);
        if (moved > 10) this.tapStart = null;
      }
    }

    onPointerUp(event) {
      if (!this.pointers.has(event.pointerId)) return;
      const wasPinching = this.mode === 'pinch';
      this.pointers.delete(event.pointerId);

      if (wasPinching && this.pointers.size < 2) {
        this.pinchStart = null;
        if (this.pointers.size === 1) {
          this.mode = this.activeContainer && this.getState(this.activeContainer).scale > 1 ? 'pan' : null;
          const remaining = Array.from(this.pointers.values())[0];
          this.lastPanPoint = remaining || null;
        } else {
          this.mode = null;
          if (this.activeContainer) this.finalizeZoom(this.activeContainer);
        }
        return;
      }

      if (this.pointers.size === 0) {
        if (this.mode === null && this.tapStart && Date.now() - this.tapStart.time < 300) {
          this.handleTap({ x: event.clientX, y: event.clientY }, this.tapStart.container);
        }
        this.mode = null;
        this.tapStart = null;
        this.lastPanPoint = null;
        if (this.activeContainer) this.finalizeZoom(this.activeContainer);
      }
    }

    finalizeZoom(container) {
      const state = this.getState(container);
      if (state.scale <= 1.02) {
        this.resetContainer(container);
        this.setZoomActive(false);
      }
    }

    handleTap(point, container) {
      if (!container) return;
      const now = Date.now();
      const last = this.lastTap;
      const isDoubleTap =
        last && last.container === container && now - last.time < 300 && Math.hypot(point.x - last.x, point.y - last.y) < 30;

      if (isDoubleTap) {
        this.lastTap = null;
        this.toggleZoom(container, point);
      } else {
        this.lastTap = { time: now, x: point.x, y: point.y, container };
      }
    }

    toggleZoom(container, point) {
      const mediaEl = container.querySelector('.product__media');
      const rect = mediaEl.getBoundingClientRect();
      const state = this.getState(container);
      this.activeContainer = container;

      if (state.scale > 1) {
        this.resetContainer(container);
        this.setZoomActive(false);
        this.announce(false);
        return;
      }

      const tapPoint = { x: point.x - rect.left, y: point.y - rect.top };
      state.scale = this.doubleTapScale;
      state.x = tapPoint.x - tapPoint.x * this.doubleTapScale;
      state.y = tapPoint.y - tapPoint.y * this.doubleTapScale;
      this.clamp(state, rect);
      this.applyTransform(container);
      this.setZoomActive(true);
      this.announce(true);
    }

    onClickCapture(event) {
      if (!this.isMobile()) return;
      const opener = event.target.closest('.product__modal-opener, .product__media-toggle');
      if (!opener) return;
      event.preventDefault();
      event.stopPropagation();
    }
  };
}
